/**
 * Transaction Context
 * Manages global transaction state
 * Task 005 - Transaction Management
 */

import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Transaction, TransactionCreateInput, TransactionUpdateInput } from '../types/transaction';
import { usePlans } from './PlanContext';
import { useAuth } from './AuthContext';
import apiClient from '../lib/apiClient';

interface TransactionContextType {
  transactions: Transaction[];
  loading: boolean;
  error: string | null;
  fetchTransactions: (
    monthPlanId?: string | null, // null = fetch all, undefined = use current month plan
    categoryId?: string,
    parentCategoryId?: string,
    accountId?: string,
    dateFrom?: string,
    dateTo?: string,
    limit?: number,
    offset?: number,
    append?: boolean // If true, append to existing transactions instead of replacing
  ) => Promise<{ transactions: Transaction[]; hasMore: boolean }>;
  fetchTransactionById: (id: string) => Promise<Transaction>;
  createTransaction: (data: TransactionCreateInput) => Promise<void>;
  updateTransaction: (id: string, data: TransactionUpdateInput) => Promise<void>;
  deleteTransaction: (id: string) => Promise<void>;
  refreshTransactions: () => Promise<void>;
  bulkDeleteTransactions: (transactionIds: string[]) => Promise<any>;
  bulkChangeCategory: (transactionIds: string[], categoryId: string) => Promise<any>;
  bulkChangeAccount: (transactionIds: string[], accountId: string) => Promise<any>;
}

const TransactionContext = createContext<TransactionContextType | undefined>(undefined);

interface TransactionProviderProps {
  children: ReactNode;
}

export const TransactionProvider: React.FC<TransactionProviderProps> = ({ children }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currentMonthPlan } = usePlans();
  const { session, loading: authLoading } = useAuth();

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!session) {
      setTransactions([]);
      setError(null);
      setLoading(false);
    }
  }, [authLoading, session?.user?.id]);

  /**
   * Fetch transactions with optional filters and pagination support
   * OPTIMIZATION: Backend already sorts by date, removed redundant client-side sorting
   * PAGINATION: Supports limit/offset and append mode for incremental loading
   */
  const fetchTransactions = useCallback(async (
    monthPlanId?: string | null, // null = explicitly fetch all, undefined = use default behavior
    categoryId?: string,
    parentCategoryId?: string,
    accountId?: string,
    dateFrom?: string,
    dateTo?: string,
    limit: number = 100,
    offset: number = 0,
    append: boolean = false
  ): Promise<{ transactions: Transaction[]; hasMore: boolean }> => {
    try {
      // Only show main loading indicator for initial fetch, not for pagination
      if (!append) {
        setLoading(true);
      }
      setError(null);

      const params = new URLSearchParams();

      // Handle month_plan_id:
      // - null: explicitly fetch all transactions (no month filter)
      // - undefined: use currentMonthPlan as default
      // - string: use provided month plan
      let planId: string | null | undefined;
      if (monthPlanId === null) {
        planId = null; // Explicitly no filter
      } else if (monthPlanId === undefined) {
        planId = currentMonthPlan?.id; // Default to current month plan
      } else {
        planId = monthPlanId; // Use provided month plan
      }

      if (planId) {
        params.append('month_plan_id', planId);
      }

      if (categoryId) params.append('category_id', categoryId);
      if (parentCategoryId) params.append('parent_category_id', parentCategoryId);
      if (accountId) params.append('account_id', accountId);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      const endpoint = params.toString() ? `/transactions?${params}` : '/transactions';
      console.log(`🌐 API Request: ${endpoint}`);
      const data = await apiClient.get(endpoint);
      const transactionsData = data.transactions || [];
      console.log(`📦 API Response: Received ${transactionsData.length} transactions`);

      // OPTIMIZATION: Backend already sorts, no need to sort again
      if (append) {
        // Append new transactions, avoiding duplicates
        setTransactions(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          const newTransactions = transactionsData.filter((t: Transaction) => !existingIds.has(t.id));
          console.log(`📋 Context append: ${prev.length} existing + ${newTransactions.length} new = ${prev.length + newTransactions.length} total`);
          return [...prev, ...newTransactions];
        });
      } else {
        // Replace all transactions
        console.log(`📋 Context replace: Setting ${transactionsData.length} transactions`);
        setTransactions(transactionsData);
      }

      // Check if there are more transactions to load
      const hasMore = transactionsData.length === limit;
      
      return { transactions: transactionsData, hasMore };
    } catch (err) {
      if (err instanceof Error && err.message === 'Authentication token not available') {
        // Session not ready; ignore and let caller retry once auth completes
        return { transactions: [], hasMore: false };
      }
      setError(err instanceof Error ? err.message : 'Failed to fetch transactions');
      console.error('Error fetching transactions:', err);
      return { transactions: [], hasMore: false };
    } finally {
      setLoading(false);
    }
  }, [currentMonthPlan?.id]);

  /**
   * Fetch a single transaction by ID
   */
  const fetchTransactionById = useCallback(async (id: string): Promise<Transaction> => {
    try {
      setLoading(true);
      setError(null);

      const transaction = await apiClient.get<Transaction>(`/transactions/${id}`);
      return transaction;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new transaction
   */
  const createTransaction = async (data: TransactionCreateInput): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      await apiClient.post('/transactions', data);

      // Note: Caller should refresh transactions with their own filters
      // We don't refresh here to avoid using wrong filters (e.g., currentMonthPlan from budget page)
      // await fetchTransactions();
      // await refreshAccounts();
      // await refreshCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update a transaction
   */
  const updateTransaction = async (id: string, data: TransactionUpdateInput): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      await apiClient.put(`/transactions/${id}`, data);

      // Note: Caller should refresh transactions with their own filters
      // We don't refresh here to avoid using wrong filters (e.g., currentMonthPlan from budget page)
      // await fetchTransactions();
      // await refreshAccounts();
      // await refreshCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete a transaction
   */
  const deleteTransaction = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      await apiClient.delete(`/transactions/${id}`);

      // Note: Caller should refresh transactions with their own filters
      // We don't refresh here to avoid using wrong filters (e.g., currentMonthPlan from budget page)
      // await fetchTransactions();
      // await refreshAccounts();
      // await refreshCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh current transactions (resets to first page)
   */
  const refreshTransactions = useCallback(async () => {
    await fetchTransactions(undefined, undefined, undefined, undefined, undefined, undefined, 100, 0, false);
  }, [fetchTransactions]);

  /**
   * Bulk delete transactions
   */
  const bulkDeleteTransactions = async (transactionIds: string[]): Promise<any> => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post('/transactions/bulk/delete', { transaction_ids: transactionIds });
      return response;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk delete transactions');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Bulk change category for transactions
   */
  const bulkChangeCategory = async (transactionIds: string[], categoryId: string): Promise<any> => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post('/transactions/bulk/category', {
        transaction_ids: transactionIds,
        category_id: categoryId
      });
      return response;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk change category');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Bulk change account for transactions
   */
  const bulkChangeAccount = async (transactionIds: string[], accountId: string): Promise<any> => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post('/transactions/bulk/account', {
        transaction_ids: transactionIds,
        account_id: accountId
      });
      return response;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk change account');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value: TransactionContextType = {
    transactions,
    loading,
    error,
    fetchTransactions,
    fetchTransactionById,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    refreshTransactions,
    bulkDeleteTransactions,
    bulkChangeCategory,
    bulkChangeAccount,
  };

  return (
    <TransactionContext.Provider value={value}>
      {children}
    </TransactionContext.Provider>
  );
};

export const useTransaction = (): TransactionContextType => {
  const context = useContext(TransactionContext);
  if (!context) {
    throw new Error('useTransaction must be used within a TransactionProvider');
  }
  return context;
};
